import { useFetch } from "@/lib/useFetch";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn, fmtHM } from "@/lib/utils";
import { Loader2, AlertTriangle, Target, TrendingUp, Info } from "lucide-react";

const eur = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n || 0);
const pct = (n) => `${(n ?? 0).toLocaleString("es-ES", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

const ROWS = [
  { key: "total", label: "PRESUPUESTO TOTAL", bold: true },
  { key: "cash_out", label: "CASH OUT" },
  { key: "horas_internas", label: "HORAS INTERNAS" },
  { key: "coste_financiero", label: "COSTES FINANCIEROS" },
  { key: "margen", label: "MARGEN DE BENEFICIO", accent: true },
];

function Cell({ cell }) {
  return (
    <>
      <td className="px-4 py-2.5 text-right tabular-nums text-sm border-l border-border">{eur(cell?.amount)}</td>
      <td className="px-4 py-2.5 text-right tabular-nums text-sm text-muted-foreground">{pct(cell?.pct)}</td>
    </>
  );
}

export default function ProjectCierre({ projectId }) {
  const { data, loading } = useFetch(`/projects/${projectId}/cierre`);

  if (loading) return <div className="flex items-center justify-center py-16 text-muted-foreground gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Calculando cierre…</div>;
  if (!data) return null;

  if (!data.has_budget) {
    return (
      <Card className="border-border shadow-none p-10 text-center" data-testid="cierre-empty">
        <AlertTriangle className="h-8 w-8 text-gold mx-auto mb-3" />
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          Este proyecto todavía no tiene un presupuesto con partidas. Crea el presupuesto en la pestaña
          <strong> Presupuesto</strong> para poder calcular el cierre de rentabilidad.
        </p>
      </Card>
    );
  }

  const r = data.resumen;
  const cumple = r.cumple;

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="cierre-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-navy">Cierre de proyecto · rentabilidad</h3>
          <p className="text-sm text-muted-foreground">Presupuesto aceptado (original) frente a lo ejecutado al cierre (final).</p>
        </div>
        <div className="flex items-center gap-2">
          {data.proposal_status === "accepted" && <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Propuesta aceptada</Badge>}
          {data.proposal_status === "sent" && <Badge variant="secondary">Propuesta enviada</Badge>}
          {(!data.proposal_status || data.proposal_status === "draft") && <Badge variant="outline">Presupuesto sin aceptar</Badge>}
        </div>
      </div>

      <Card className="border-border shadow-none overflow-x-auto" data-testid="cierre-table">
        <table className="w-full border-collapse min-w-[720px]">
          <thead>
            <tr>
              <th className="px-4 py-3 text-left"></th>
              <th colSpan={2} className="px-4 py-3 text-center text-xs font-bold tracking-wide text-navy bg-navy/5 border-l border-border">PRESUPUESTO ORIGINAL</th>
              <th colSpan={2} className="px-4 py-3 text-center text-xs font-bold tracking-wide text-navy bg-navy/5 border-l border-border">PRESUPUESTO FINAL</th>
              <th colSpan={2} className="px-4 py-3 text-center text-xs font-bold tracking-wide text-emerald-700 bg-emerald-500/10 border-l border-border">RESULTADO GESTIÓN</th>
            </tr>
            <tr className="text-[11px] uppercase text-muted-foreground">
              <th className="px-4 py-2 text-left font-medium"></th>
              <th className="px-4 py-2 text-right font-medium border-l border-border">Importe s/IVA</th>
              <th className="px-4 py-2 text-right font-medium">% del total</th>
              <th className="px-4 py-2 text-right font-medium border-l border-border">Importe s/IVA</th>
              <th className="px-4 py-2 text-right font-medium">% del total</th>
              <th className="px-4 py-2 text-right font-medium border-l border-border">Importe s/IVA</th>
              <th className="px-4 py-2 text-right font-medium">% del total</th>
            </tr>
          </thead>
          <tbody>
            {ROWS.map((row) => (
              <tr key={row.key}
                  className={cn("border-t border-border", row.accent && "bg-gold/8", row.bold && "bg-muted/40")}
                  data-testid={`cierre-row-${row.key}`}>
                <td className={cn("px-4 py-2.5 text-sm", (row.bold || row.accent) ? "font-semibold text-navy" : "text-foreground")}>{row.label}</td>
                <Cell cell={data.original[row.key]} />
                <Cell cell={data.final[row.key]} />
                <Cell cell={data.gestion[row.key]} />
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {data.hours?.approx && (
        <p className="flex items-start gap-2 text-xs text-muted-foreground">
          <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          Horas internas ejecutadas estimadas con tarifa media ({eur(data.hours.rate)}/h · {fmtHM(data.hours.total)} de Teamleader),
          porque Teamleader no desglosa las horas por persona. El coste será exacto cuando se registren las horas por trabajador en la app.
        </p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-border shadow-none p-5" data-testid="cierre-gestion">
          <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wide"><TrendingUp className="h-4 w-4 text-emerald-700" /> Resultado de gestión</div>
          <div className="mt-2 text-3xl font-bold text-emerald-700 tabular-nums">{pct(r.resultado_gestion_pct)}</div>
          <p className="text-xs text-muted-foreground mt-1">Mejora del margen frente al presupuesto original ({pct(r.original_margen_pct)} → {pct(r.final_margen_pct)}).</p>
        </Card>
        <Card className="border-border shadow-none p-5 bg-amber-50/60" data-testid="cierre-objetivo">
          <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wide"><Target className="h-4 w-4 text-gold" /> Objetivo de empresa</div>
          <div className="mt-2 text-3xl font-bold text-navy tabular-nums">{pct(r.objetivo)}</div>
          <p className="text-xs text-muted-foreground mt-1">Margen objetivo para considerar el proyecto rentable.</p>
        </Card>
        <Card className={cn("border-border shadow-none p-5", cumple ? "bg-emerald-50/70" : "bg-rose-50/70")} data-testid="cierre-resultado">
          <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wide">Resultado</div>
          <div className={cn("mt-2 text-3xl font-bold tabular-nums", cumple ? "text-emerald-700" : "text-rose-700")}>{pct(r.resultado)}</div>
          <p className={cn("text-xs mt-1 font-medium", cumple ? "text-emerald-700" : "text-rose-700")}>
            {cumple ? "✓ Cumple el objetivo" : `✗ Por debajo del objetivo (${pct(r.objetivo)})`}
          </p>
        </Card>
      </div>
    </div>
  );
}
